import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/auth';
import './auth.css';
import Message from '../../components/ui/Message';
import { InstanceLogo } from '../../components/ui/InstanceLogo';
import { BRAND_NAME } from '../../utils/appConfig';

// Caracteres especiales permitidos (mismos que valida el sistema)
const SPECIAL_CHARS = `!"#$%&'()*+,-./:;<=>?@[\\]^_\`{|}~`;
const SPECIAL_REGEX = /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/;
const ALLOWED_REGEX = /^[A-Za-z\d!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]*$/;

// Requisitos de la nueva contraseña (se marcan en verde mientras se escribe)
export const passwordRules = (current: string, password: string, confirm: string) => [
  { key: 'length', label: 'Entre 6 y 20 caracteres', ok: password.length >= 6 && password.length <= 20 },
  { key: 'lower', label: 'Al menos una letra minúscula', ok: /[a-z]/.test(password) },
  { key: 'upper', label: 'Al menos una letra mayúscula', ok: /[A-Z]/.test(password) },
  { key: 'number', label: 'Al menos un número', ok: /\d/.test(password) },
  { key: 'special', label: 'Al menos un carácter especial', ok: SPECIAL_REGEX.test(password) },
  { key: 'allowed', label: 'Sin espacios, tildes ni ñ', ok: password.length > 0 && ALLOWED_REGEX.test(password) },
  { key: 'different', label: 'Distinta a la contraseña actual', ok: password.length > 0 && password !== current },
  { key: 'match', label: 'La confirmación coincide', ok: confirm.length > 0 && password === confirm },
];

type PasswordFieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: string;
  autoFocus?: boolean;
};

const PasswordField = ({ id, label, value, onChange, autoComplete, autoFocus }: PasswordFieldProps) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="mb-3">
      <label htmlFor={id} className="form-label small text-muted mb-1">{label}</label>
      <div className="input-group">
        <span className="input-group-text"><i className="bi bi-lock" /></span>
        <input
          type={visible ? 'text' : 'password'}
          className="form-control"
          id={id}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          className="btn auth-toggle border"
          onClick={() => setVisible(prev => !prev)}
          title={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        >
          <i className={visible ? 'bi bi-eye-slash' : 'bi bi-eye'} />
        </button>
      </div>
    </div>
  );
};

const ChangePassword = () => {
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localErrorMessage, setLocalErrorMessage] = useState('');
  const navigate = useNavigate();

  const { status, errorMessage, successMessage, removeError, changePassword } = useContext(AuthContext);

  const rules = passwordRules(password, newPassword, confirmPassword);
  const allRulesOk = rules.every(rule => rule.ok);
  const canSubmit = password.length > 0 && allRulesOk;

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmit) {
      setLocalErrorMessage('La nueva contraseña no cumple los requisitos.');
      return;
    }

    const changed = await changePassword(password, newPassword);

    if (changed) {
      localStorage.removeItem('token');
      navigate('/login', { replace: true });
    }
  };

  if (status === 'checking') {
    return (
      <div className="auth-page">
        <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-3 text-muted">Actualizando la contraseña...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <main className="auth-main">
        <div className="auth-card">
          <div className="text-center mb-4">
            <InstanceLogo className="auth-logo" />
            <h4 className="mb-1">Cambiar contraseña</h4>
            <p className="text-muted mb-0">Por seguridad, defina una contraseña nueva para continuar.</p>
          </div>

          <form onSubmit={handleChangePassword} autoComplete="off" noValidate>
            <PasswordField id="currentPassword" label="Contraseña actual" value={password} onChange={setPassword}
              autoComplete="current-password" autoFocus />
            <PasswordField id="newPassword" label="Nueva contraseña" value={newPassword} onChange={setNewPassword}
              autoComplete="new-password" />
            <PasswordField id="confirmPassword" label="Confirmar nueva contraseña" value={confirmPassword} onChange={setConfirmPassword}
              autoComplete="new-password" />

            <div className="border rounded-3 p-3 mb-4 bg-light">
              <div className="small fw-semibold mb-2">Requisitos de la contraseña</div>
              <ul className="auth-rules">
                {rules.map(rule => (
                  <li key={rule.key} className={rule.ok ? 'is-ok' : ''}>
                    <i className={`bi ${rule.ok ? 'bi-check-circle-fill' : 'bi-circle'}`} />
                    {rule.label}
                  </li>
                ))}
              </ul>
              <div className="small text-muted mt-2">
                Caracteres especiales: <code>{SPECIAL_CHARS}</code>. Evite datos personales; pruebe una frase como <b>Casa!2023&amp;Perro</b>.
              </div>
            </div>

            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-secondary w-50" onClick={() => navigate('/login', { replace: true })}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary w-50" disabled={!canSubmit}>
                <i className="bi bi-check-lg me-1" /> Cambiar
              </button>
            </div>
          </form>
        </div>
      </main>

      <footer className="auth-footer">© {new Date().getFullYear()} {BRAND_NAME}</footer>

      {errorMessage.length > 0 && (
        <Message type='error' message={errorMessage} closeDialog={removeError} handleOk={removeError} backgroundColor='rgba(0,0,0,0.4)' />
      )}

      {localErrorMessage.length > 0 && (
        <Message type='error' message={localErrorMessage} closeDialog={() => setLocalErrorMessage('')} handleOk={() => setLocalErrorMessage('')} backgroundColor='rgba(0,0,0,0.4)' />
      )}

      {successMessage.length > 0 && (
        <Message type='success' message={successMessage} closeDialog={removeError} handleOk={removeError} backgroundColor='rgba(0,0,0,0.4)' />
      )}
    </div>
  );
};

export default ChangePassword;
